import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import IlokLinkForm, { IlokLinkResult, ilokLinkMessage } from "./IlokLinkForm"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// a text link in button clothing (same reset as ImageField's inline actions)
const LINK_BUTTON_STYLE = { background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" } as const

// while an emailed verification is outstanding the dialog refetches the
// linked accounts on this cadence (and whenever the tab regains focus —
// the user confirms in another tab and comes back), giving up after the
// cap so an abandoned dialog doesn't poll forever
const PENDING_POLL_MS = 5_000
const PENDING_POLL_CAP_MS = 15 * 60_000

// The Library's "Get iLok license" workflow: pick one of the user's
// confirmed linked iLok accounts (or, with a single link, just see where
// the license lands) and confirm — the backend deposits a perpetual iLok
// license for the entitlement's product into that account (once per
// license, ever). "Link another iLok account" swaps the dialog to the
// shared link form. An account PACE links on the spot becomes the selected
// target as soon as the refetched list has it; one that needs the emailed
// verification is waited for — the dialog polls linkedIlokIds (via
// onRefreshAccounts) until the confirmed account appears, then selects it.
// onAccountsChanged fires once per completed link so the host can refresh
// whatever a link may have auto-issued.
export default function IlokDepositDialog({ entitlementId, skuName, linkedIlokIds, accessToken, onClose, onDone, onRefreshAccounts, onAccountsChanged }: {
  entitlementId: string,
  skuName: string,
  linkedIlokIds: string[],
  accessToken: string,
  onClose: () => void,
  onDone: () => void,
  onRefreshAccounts: () => void,
  onAccountsChanged: () => void,
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [view, setView] = useState<"deposit" | "link">("deposit")
  const [step, setStep] = useState<"form" | "saving" | "success" | "error">("form")
  const [ilokId, setIlokId] = useState(linkedIlokIds[0] ?? "")
  const [linkBusy, setLinkBusy] = useState(false)
  const [notice, setNotice] = useState("")
  const [pendingIlokId, setPendingIlokId] = useState<string | null>(null)
  // set while waiting for the emailed verification of pendingIlokId
  const [awaiting, setAwaiting] = useState<{ email: string, since: number } | null>(null)
  const [awaitTimedOut, setAwaitTimedOut] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => { dialogRef.current?.showModal() }, [])

  // a freshly linked account is selected once the refetched list has it;
  // when it was the one being waited for, the wait ends here
  useEffect(() => {
    if (pendingIlokId !== null && linkedIlokIds.includes(pendingIlokId)) {
      setIlokId(pendingIlokId)
      setPendingIlokId(null)
      if (awaiting !== null) {
        setAwaiting(null)
        setNotice(`${pendingIlokId} is confirmed and linked. It's now the selected account.`)
        onAccountsChanged()
      }
    } else if (ilokId === "" && linkedIlokIds.length > 0) {
      setIlokId(linkedIlokIds[0])
    }
  }, [linkedIlokIds, pendingIlokId, ilokId, awaiting, onAccountsChanged])

  // the wait itself: poll, refetch on focus, stop at the cap
  useEffect(() => {
    if (awaiting === null) return
    const refresh = () => {
      if (Date.now() - awaiting.since > PENDING_POLL_CAP_MS) {
        setAwaiting(null)
        setAwaitTimedOut(true)
        return
      }
      onRefreshAccounts()
    }
    const onVisible = () => { if (document.visibilityState === "visible") refresh() }
    const timer = window.setInterval(refresh, PENDING_POLL_MS)
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("focus", refresh)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("focus", refresh)
    }
  }, [awaiting, onRefreshAccounts])

  const busy = step === "saving" || linkBusy
  // One linked account leaves nothing to choose: name the destination in
  // the hint instead of showing a single-option select.
  const singleAccount = linkedIlokIds.length === 1

  const handleLinked = (result: IlokLinkResult) => {
    setNotice("")
    setAwaiting(null)
    setAwaitTimedOut(false)
    switch (result.kind) {
      case "linked":
        setPendingIlokId(result.ilokId)
        setNotice(`${result.ilokId} is linked. ${result.issued > 0
          ? `${result.issued === 1 ? "One license was" : `${result.issued} licenses were`} added to your account.`
          : "It's now the selected account."}`)
        onAccountsChanged()
        break
      case "alreadyLinked":
        setPendingIlokId(result.ilokId)
        setNotice(ilokLinkMessage(result))
        break
      case "pending":
        setPendingIlokId(result.ilokId)
        setAwaiting({ email: result.email, since: Date.now() })
        break
    }
    setView("deposit")
  }

  const handleSubmit = async () => {
    setStep("saving")
    setErrorMessage("")
    try {
      const result = await Api.requestIlokDeposit({ entitlementId, ilokId, accessToken })
      setSuccessMessage(result.alreadyDeposited
        ? "An iLok license for this product has already been deposited."
        : `Deposited to ${ilokId} — it will appear in iLok License Manager shortly.`)
      onDone()
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy}>&times;</button>
        <h2 className="redeem-dialog-title">{view === "link" ? "Link iLok Account" : "Get iLok License"}</h2>

        {view === "link" ? (
          <IlokLinkForm
            accessToken={accessToken}
            cancelLabel="Back"
            onCancel={() => setView("deposit")}
            onBusyChange={setLinkBusy}
            onLinked={handleLinked}
          />
        ) : step === "success" ? (
          <>
            <p className="generate-codes-success">{successMessage}</p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Close</button>
            </div>
          </>
        ) : (
          <>
            {singleAccount ? (
              <p className="generate-codes-hint">
                This deposits a perpetual iLok license for <strong>{skuName}</strong> into
                your iLok account <strong>{ilokId}</strong>. It can be done once per license.
              </p>
            ) : (
              <>
                <p className="generate-codes-hint">
                  This deposits a perpetual iLok license for <strong>{skuName}</strong> into
                  the chosen iLok account. It can be done once per license.
                </p>
                <div className="generate-codes-form">
                  <div className="generate-codes-field">
                    <span className="generate-codes-label">iLok account</span>
                    <select
                      value={ilokId}
                      onChange={(e) => setIlokId(e.target.value)}
                      className="generate-codes-input"
                      disabled={busy}
                      style={{ width: "auto" }}
                    >
                      {linkedIlokIds.map((id) => (
                        <option key={id} value={id}>{id}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
            <p className="generate-codes-hint">
              <button type="button" className="card-link" style={LINK_BUTTON_STYLE} onClick={() => setView("link")} disabled={busy}>
                Link another iLok account
              </button>
            </p>
            {notice && <p className="generate-codes-success">{notice}</p>}
            {awaiting !== null && pendingIlokId !== null && (
              <p className="generate-codes-hint">
                <Spinner size={12} /> Waiting for you to confirm <strong>{pendingIlokId}</strong> — we've
                emailed a verification link to {awaiting.email}, the address on your ilok.com
                account. Click it and the account will be selected here.
              </p>
            )}
            {awaitTimedOut && pendingIlokId !== null && (
              <p className="generate-codes-hint">
                Still waiting for <strong>{pendingIlokId}</strong> to be confirmed. Once you've clicked
                the emailed link, reopen this dialog to pick it.
              </p>
            )}
            {step === "error" && errorMessage && <p className="generate-codes-error">{errorMessage}</p>}
            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" onClick={handleSubmit} disabled={ilokId === "" || busy}>
                {busy ? <><Spinner size={12} /> Depositing…</> : "Deposit iLok license"}
              </button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}
